import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

import type { NavigationSectionGroup } from '@/components/Navigation/NavigationCard';
import { CForm } from '@/features/c-form/CForm';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { useSoftware, useUpdateSoftwareArchitectureMutator } from '../-query';

import { ExecutionRequirementsSection } from './-components/ExecutionRequirementsSection';
import { InstallInformationSection } from './-components/InstallInformationSection';
import { SecurityComplianceSection } from './-components/SecurityComplianceSection';
import { SoftwareComponentSection } from './-components/SoftwareComponentSection';
import { SoftwareDependencySection } from './-components/SoftwareDependencySection';
import { SoftwareInstanceSection } from './-components/SoftwareInstanceSection';
import { UninstallInformationSection } from './-components/UninstallInformationSection';
import { VersionBuildInformationSection } from './-components/VersionBuildInformationSection';
import {
  SoftwareArchitectureStore,
  useSoftwareArchitectureStore,
} from './-store/store';

export const Route = createFileRoute('/_staff/software/$id/_$id/architecture/')(
  {
    component: SoftwareArchitecture,
  },
);

function SoftwareArchitecture() {
  const store = SoftwareArchitectureStore.createStore();
  return (
    <SoftwareArchitectureStore.Provider value={store}>
      <Content />
    </SoftwareArchitectureStore.Provider>
  );
}

function Content() {
  const { id } = Route.useParams();
  const { data } = useSoftware(id);

  const { values, actions } = useSoftwareArchitectureStore();
  useEffect(() => actions.reset(data), [actions, data]);

  const updateSoftwareArchitectureMutation =
    useUpdateSoftwareArchitectureMutator(id);

  const handleSave = useCallback(async () => {
    if (!actions.validate()) {
      throw new Error('Validation failed');
    }
    const result = await updateSoftwareArchitectureMutation.mutateAsync(values);
    return result;
  }, [actions, updateSoftwareArchitectureMutation, values]);

  const handleReset = useCallback(() => {
    actions.reset(data);
  }, [actions, data]);

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.profile.architecture.navigation',
  });

  const navigationSectionGroups: NavigationSectionGroup[] = [
    {
      titleLabel: t('versionBuildInformation'),
      to: '',
      hash: 'version-build-information',
    },
    {
      titleLabel: t('softwareComponent'),
      to: '',
      hash: 'software-component',
    },
    {
      titleLabel: t('softwareDependency'),
      to: '',
      hash: 'software-dependency',
    },
    {
      titleLabel: t('softwareInstance'),
      to: '',
      hash: 'software-instance',
    },
    {
      titleLabel: t('installInformation'),
      to: '',
      hash: 'install-information',
    },
    {
      titleLabel: t('executionRequirements'),
      to: '',
      hash: 'execution-requirements',
    },
    {
      titleLabel: t('securityCompliance'),
      to: '',
      hash: 'security-compliance',
    },
    {
      titleLabel: t('uninstallInformation'),
      to: '',
      hash: 'uninstall-information',
    },
  ];

  return (
    <CForm
      editUnit="section"
      itemLayout="label"
      navigationSectionGroups={navigationSectionGroups}
      onSave={handleSave}
      onReset={handleReset}
    >
      <VersionBuildInformationSection />
      <SoftwareComponentSection />
      <SoftwareDependencySection />
      <SoftwareInstanceSection />
      <InstallInformationSection />
      <ExecutionRequirementsSection />
      <SecurityComplianceSection />
      <UninstallInformationSection />
    </CForm>
  );
}
